/**
 * Modal para mostrar detalles de usuario
 * Sistema Klever Octopus - Betel Natural
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Modal } from '../../components/UI/Modal';

const UserDetailModal = ({ user, isOpen, onClose, onEdit = null }) => {
  if (!user) return null;

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRoleName = () => {
    if (user.role) {
      return user.role.display_name || user.role.name;
    }
    return 'Sin rol asignado';
  };

  const getRoleColor = () => {
    return user.role?.color || '#6c757d';
  };

  return (
    <Modal
      title={`Detalles del Usuario - ${user.nombre} ${user.apellido}`}
      isOpen={isOpen}
      onClose={onClose}
      size="large"
    >
      <div className="user-detail-modal">
        <div className="detail-sections">
          {/* Información Personal */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-user"></i>
              Información Personal
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Nombre Completo</span>
                <span className="item-value">{user.nombre} {user.apellido}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Email</span>
                <span className="item-value">{user.email}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Teléfono</span>
                <span className="item-value">{user.telefono || 'No especificado'}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Estado</span>
                <span className={`item-value ${user.is_active ? 'status-active' : 'status-inactive'}`}>
                  <i className={`fas fa-circle ${user.is_active ? 'text-success' : 'text-danger'}`}></i>
                  {user.is_active ? 'Activo' : 'Inactivo'}
                </span>
              </div>
            </div>
          </div>

          {/* Rol y Permisos */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-user-shield"></i>
              Rol y Permisos
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">Rol Asignado</span>
                <span className="item-value">
                  <span 
                    className="role-badge"
                    style={{ backgroundColor: getRoleColor(), color: 'white' }}
                  >
                    {getRoleName()}
                  </span>
                </span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Nivel de Acceso</span>
                <span className="item-value">
                  {user.role?.nivel_acceso ? (
                    <span className={`level-badge level-${user.role.nivel_acceso}`}>
                      Nivel {user.role.nivel_acceso}
                    </span>
                  ) : 'No definido'}
                </span>
              </div>
            </div>

            {user.role?.description && (
              <div className="detail-item full-width">
                <span className="item-label">Descripción del Rol</span>
                <span className="item-value">{user.role.description}</span>
              </div>
            )}

            {/* Permisos adicionales */}
            {user.permissions && user.permissions.length > 0 && (
              <div className="detail-item full-width">
                <span className="item-label">Permisos Adicionales</span>
                <div className="permissions-list">
                  {user.permissions.map(permission => (
                    <span key={permission.id} className="permission-tag">
                      {permission.display_name || permission.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Información de Vendedor (si aplica) */}
          {user.role && user.role.name === 'vendedor' && user.vendedor_data && (
            <div className="detail-section">
              <h3 className="section-title">
                <i className="fas fa-percentage"></i>
                Información de Vendedor
              </h3>
              
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="item-label">Comisión</span>
                  <span className="item-value">{user.vendedor_data.comision_porcentaje}%</span>
                </div>
                
                <div className="detail-item">
                  <span className="item-label">Frecuencia de Pago</span>
                  <span className="item-value">
                    {user.vendedor_data.tipo_pago?.charAt(0).toUpperCase() + user.vendedor_data.tipo_pago?.slice(1)}
                  </span>
                </div>
                
                <div className="detail-item">
                  <span className="item-label">Forma de Pago</span>
                  <span className="item-value">
                    {user.vendedor_data.forma_pago?.charAt(0).toUpperCase() + user.vendedor_data.forma_pago?.slice(1)}
                  </span>
                </div>
              </div>

              {user.vendedor_data.datos_bancarios && (
                <>
                  <h4 className="subsection-title">Datos Bancarios</h4>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="item-label">Banco</span>
                      <span className="item-value">
                        {user.vendedor_data.datos_bancarios.banco || 'No especificado'}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="item-label">Tipo de Cuenta</span>
                      <span className="item-value">
                        {user.vendedor_data.datos_bancarios.tipo_cuenta || 'No especificado'}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="item-label">Titular</span>
                      <span className="item-value">
                        {user.vendedor_data.datos_bancarios.titular || 'No especificado'}
                      </span>
                    </div>
                    
                    <div className="detail-item">
                      <span className="item-label">Número de Cuenta</span>
                      <span className="item-value">
                        {user.vendedor_data.datos_bancarios.numero_cuenta ? 
                          `****${user.vendedor_data.datos_bancarios.numero_cuenta.slice(-4)}` : 
                          'No especificado'
                        }
                      </span>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Información del Sistema */}
          <div className="detail-section">
            <h3 className="section-title">
              <i className="fas fa-info-circle"></i>
              Información del Sistema
            </h3>
            
            <div className="detail-grid">
              <div className="detail-item">
                <span className="item-label">ID de Usuario</span>
                <span className="item-value">#{user.id}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Fecha de Creación</span>
                <span className="item-value">{formatDate(user.created_at)}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Última Actualización</span>
                <span className="item-value">{formatDate(user.updated_at)}</span>
              </div>
              
              <div className="detail-item">
                <span className="item-label">Último Acceso</span>
                <span className="item-value">{formatDate(user.last_login_at) || 'Nunca'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Acciones */}
        {onEdit && (
          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={onEdit}
            >
              <i className="fas fa-edit"></i>
              Editar Usuario
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
};

UserDetailModal.propTypes = {
  user: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func
};

export default UserDetailModal;
